import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Card, Spinner } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa';
import api from '../../services/api';
import Swal from 'sweetalert2';

const BankForm = () => {
  const { bankId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validated, setValidated] = useState(false);
  const [formData, setFormData] = useState({
    bank: '',
    bank_slug: ''
  });
  
  const isEditMode = !!bankId;
  const title = isEditMode ? 'Edit Bank' : 'Add Bank';

  useEffect(() => {
    document.title = title;
    if (isEditMode) {
      fetchBankDetails();
    }
  }, [bankId, title]);

  const fetchBankDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/banks/${bankId}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching bank details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch bank details'
      });
      navigate('/admin/banks');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value === '' ? 0 : parseInt(value, 10)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    // Form validation
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    setValidated(true);
    setSubmitting(true);
    
    try {
      if (isEditMode) {
        await api.put(`/admin/banks/${bankId}`, formData);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Bank updated successfully'
        });
      } else {
        await api.post('/admin/banks', formData);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Bank created successfully'
        });
      }
      navigate('/admin/banks');
    } catch (error) {
      console.error('Error saving bank:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to save bank'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading bank details...</p>
      </div>
    );
  }

  return (
    <Container className="py-4">
      <Card className="shadow-sm">
        <Card.Header as="h5" className="bg-white">
          <Row className="align-items-center">
            <Col>{title}</Col>
            <Col xs="auto">
              <Button 
                variant="link" 
                className="p-0"
                onClick={() => navigate('/admin/banks')}
              >
                <FaArrowLeft className="me-2" /> Back to Banks
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Bank Name*</Form.Label>
              <Form.Control
                type="text"
                name="bank"
                value={formData.bank}
                onChange={handleInputChange}
                placeholder="Enter bank name"
                required
              />
              <Form.Control.Feedback type="invalid">
                Please enter bank name
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label>Bank Slug</Form.Label>
              <Form.Control
                type="text"
                name="bank_slug"
                value={formData.bank_slug || ''}
                onChange={handleInputChange}
                placeholder="Enter bank slug (auto-generated if empty)"
              />
              <Form.Text className="text-muted">
                If left empty, will be auto-generated from bank name
              </Form.Text>
            </Form.Group>

            <div className="d-flex gap-2">
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEditMode ? 'Update Bank' : 'Add Bank'
                )}
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/admin/banks')}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default BankForm;